const JSON_HEADERS = {
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

export class VfCartChallengeError extends Error {
  constructor(message = 'The cart request was interrupted by a storefront challenge.') {
    super(message);
    this.name = 'VfCartChallengeError';
  }
}

class VfCartRequestError extends Error {
  constructor(message, details = null) {
    super(message);
    this.name = 'VfCartRequestError';
    this.details = details;
  }
}

/** @param {string} url */
function jsonUrl(url) {
  return url.endsWith('.js') ? url : `${url}.js`;
}

/** @param {Response} response */
async function readJson(response) {
  const challenged = response.status === 429 || response.headers.get('cf-mitigated') === 'challenge';

  if (challenged) throw new VfCartChallengeError();

  const body = await response.text();
  let data;
  try {
    data = JSON.parse(body);
  } catch {
    throw new VfCartChallengeError();
  }
  if (!response.ok || data.status) {
    throw new VfCartRequestError(data.description || data.message || 'The cart could not be updated.', data);
  }

  return data;
}

/** @param {string} tag @param {string} className @param {string} [text] */
function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class VfCartDrawer extends HTMLElement {
  /** @type {Promise<unknown> | null} */
  #request = null;
  /** @type {HTMLElement | null} */
  #returnFocus = null;
  #cart = null;
  #pending = new Map();
  #activeChange = null;
  #adding = false;
  #checkingOut = false;
  #syncFailed = false;

  connectedCallback() {
    try {
      this.#cart = JSON.parse(this.querySelector('[data-vf-cart-initial]')?.textContent || 'null');
    } catch {}
    window.addEventListener('pageshow', this.#resetCheckout);
    this.dialog?.addEventListener('submit', this.#handleSubmit);
    this.trigger?.addEventListener('click', this.open);
    this.dialog?.addEventListener('click', this.#handleDialogClick);
    this.content?.addEventListener('click', this.#handleContentClick);
    this.content?.addEventListener('keydown', this.#handleQuantityKeydown);
    this.content?.addEventListener('focusout', this.#handleQuantityBlur);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.#resetCheckout);
    this.dialog?.removeEventListener('submit', this.#handleSubmit);
    this.trigger?.removeEventListener('click', this.open);
    this.dialog?.removeEventListener('click', this.#handleDialogClick);
    this.content?.removeEventListener('click', this.#handleContentClick);
    this.content?.removeEventListener('keydown', this.#handleQuantityKeydown);
    this.content?.removeEventListener('focusout', this.#handleQuantityBlur);
  }

  get trigger() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-vf-cart-trigger]'));
  }

  get dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-vf-cart-dialog]'));
  }

  get content() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-vf-cart-content]'));
  }

  get status() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-vf-cart-status]'));
  }

  get labels() {
    return {
      title: this.dataset.labelTitle || 'Your cart',
      emptyTitle: this.dataset.labelEmptyTitle || 'Your cart is empty',
      emptyText: this.dataset.labelEmptyText || '',
      continue: this.dataset.labelContinue || 'Continue shopping',
      subtotal: this.dataset.labelSubtotal || 'Subtotal',
      shipping: this.dataset.labelShipping || '',
      quantityUnavailable: this.dataset.labelQuantityUnavailable || '',
      checkout: this.dataset.labelCheckout || 'Check out',
      decrease: this.dataset.labelDecrease || 'Decrease quantity',
      increase: this.dataset.labelIncrease || 'Increase quantity',
      remove: this.dataset.labelRemove || 'Remove',
      error: this.dataset.labelError || 'Cart could not be updated.',
      viewCart: this.dataset.labelViewCart || 'View cart',
    };
  }

  open = () => {
    if (!this.dialog || this.dialog.open) return;
    this.#returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : this.trigger;
    this.dialog.showModal();
    this.dialog.focus();
  };

  close = () => {
    if (!this.dialog?.open) return;
    this.dialog.close();
    this.#returnFocus?.focus();
  };

  /** @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    const target = event.target;
    if (target === this.dialog || (target instanceof Element && target.closest('[data-vf-cart-close]'))) this.close();
  };

  /** @param {MouseEvent} event */
  #handleContentClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-vf-cart-quantity]') : null;
    if (!(target instanceof HTMLButtonElement)) return;

    const key = target.dataset.key;
    const quantity = Number(target.dataset.quantity);
    if (!key || !Number.isFinite(quantity)) return;

    this.change(key, Math.max(0, quantity));
  };

  /** @param {KeyboardEvent} event */
  #handleQuantityKeydown = (event) => {
    if (event.key !== 'Enter') return;
    const input = event.target instanceof Element ? event.target.closest('[data-vf-cart-quantity-input]') : null;
    if (!(input instanceof HTMLInputElement)) return;

    event.preventDefault();
    this.#commitQuantityInput(input);
  };

  /** @param {FocusEvent} event */
  #handleQuantityBlur = (event) => {
    const input = event.target instanceof Element ? event.target.closest('[data-vf-cart-quantity-input]') : null;
    if (input instanceof HTMLInputElement) this.#commitQuantityInput(input);
  };

  /** @param {HTMLInputElement} input */
  #commitQuantityInput(input) {
    const key = input.dataset.key;
    const quantity = Number(input.value);
    const currentQuantity = Number(input.dataset.quantity);
    if (!key || !Number.isInteger(quantity) || quantity < 1) {
      input.value = input.dataset.quantity || '1';
      return;
    }
    if (quantity === currentQuantity) return;

    this.change(key, quantity);
  }

  /** @param {FormData} formData @param {{optimisticItemCount?: number}} [options] */
  add(formData, options = {}) {
    this.open();
    return this.#enqueueAdd(formData, options);
  }


  #enqueueAdd(formData, { optimisticItemCount = 0 } = {}) {
    if (this.#request) {
      return this.#request.catch(() => {}).then(() => this.#enqueueAdd(formData, { optimisticItemCount }));
    }

    this.#clearStatus();
    if (optimisticItemCount > 0) this.#announceOptimistic(optimisticItemCount);
    this.#adding = true;
    this.#setBusy(true);

    this.#request = this.#add(formData, optimisticItemCount)
      .catch(async (error) => {
        console.error('Cart add failed:', error);
        this.#syncFailed = !(await this.#restoreCartCount());
        this.#showError(error instanceof VfCartRequestError ? error.message : this.labels.error);
        throw error;
      })
      .finally(() => {
        this.#request = null;
        this.#adding = false;
        this.#setBusy(false);
      });

    return this.#request;
  }

  /** @param {FormData} formData @param {number} optimisticItemCount */
  async #add(formData, optimisticItemCount) {
    const addUrl = this.dataset.addUrl;
    if (!addUrl) throw new VfCartChallengeError('Cart add URL is missing.');

    formData.set('sections', 'vf-cart-data');
    formData.set('sections_url', window.location.pathname);
    const response = await fetch(jsonUrl(addUrl), {
      method: 'POST',
      headers: JSON_HEADERS,
      body: formData,
      credentials: 'same-origin',
    });
    const added = await readJson(response);
    const cart = this.#cartFromSection(added.sections?.['vf-cart-data']) || (await this.#fetchCart());

    this.render(cart);
    this.#announceCart(cart);
    return { added, cart };
  }

  #handleSubmit = (event) => {
    if (this.#request || this.#pending.size || this.#syncFailed || this.#checkingOut) {
      event.preventDefault();
      return;
    }
    this.#checkingOut = true;
    this.querySelector('.vf-cart__checkout')?.setAttribute('aria-busy', 'true');
  };
  #resetCheckout = () => {
    this.#checkingOut = false;
    this.querySelector('.vf-cart__checkout')?.removeAttribute('aria-busy');
  };
  #cartFromSection(html) {
    if (!html) return null;
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cart = JSON.parse(doc.querySelector('[data-vf-cart-data]')?.textContent || 'null');
      return cart && Array.isArray(cart.items) && Number.isFinite(cart.item_count) ? cart : null;
    } catch {
      return null;
    }
  }
  change(key, quantity) {
    if (this.#adding || !this.#cart || !Number.isInteger(quantity) || quantity < 0) return;
    this.#pending.set(key, quantity);
    this.#clearStatus();
    this.#renderPending();
    if (!this.#request) {
      this.#request = this.#drainChanges().finally(() => {
        this.#request = null;
        this.#setBusy(false);
      });
    }
    return this.#request;
  }
  async #drainChanges() {
    try {
      while (this.#pending.size) {
        const [key, quantity] = this.#pending.entries().next().value;
        this.#pending.delete(key);
        this.#activeChange = { key, quantity };
        const item = this.#cart.items.find((item) => item.key === key);
        if (!item || item.quantity === quantity) {
          this.#activeChange = null;
          continue;
        }
        this.#cart = await this.#change(key, quantity);
        this.#activeChange = null;
        this.#renderPending();
      }
      this.render(this.#cart);
      this.#announceCart(this.#cart);
    } catch (error) {
      console.error('Cart update failed:', error);
      const rejectedChange = this.#activeChange;
      this.#pending.clear();
      this.#activeChange = null;
      this.render(this.#cart);
      this.#syncFailed = !(await this.#restoreCartCount());
      const confirmedItem = this.#cart?.items.find((item) => item.key === rejectedChange?.key);
      const quantityUnavailable = !this.#syncFailed && error instanceof VfCartRequestError &&
        Number(error.details?.status) === 422 && confirmedItem && rejectedChange.quantity > confirmedItem.quantity;
      const message = quantityUnavailable && this.labels.quantityUnavailable
        ? this.labels.quantityUnavailable.replace('[quantity]', String(confirmedItem.quantity))
        : error instanceof VfCartRequestError ? error.message : this.labels.error;
      this.#showError(message);
    }
  }
  #renderPending() {
    const changes = new Map(this.#activeChange ? [[this.#activeChange.key, this.#activeChange.quantity]] : []);
    for (const [key, quantity] of this.#pending) changes.set(key, quantity);
    const items = this.#cart.items
      .map((item) =>
        changes.has(item.key) ? { ...item, quantity: changes.get(item.key), final_line_price: null } : item
      )
      .filter((item) => item.quantity > 0);
    const cart = {
      ...this.#cart,
      items,
      item_count: items.reduce((count, item) => count + item.quantity, 0),
      total_price: changes.size ? null : this.#cart.total_price,
    };
    this.#renderCart(cart);
    this.#setBusy(true);
    const icon = document.querySelector('cart-icon');
    icon?.renderCartBubble?.(cart.item_count, false, false);
  }
  async #change(key, quantity) {
    const changeUrl = this.dataset.changeUrl;
    if (!changeUrl) throw new VfCartRequestError(this.labels.error);
    const response = await fetch(jsonUrl(changeUrl), {
      method: 'POST',
      headers: { ...JSON_HEADERS, 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
      credentials: 'same-origin',
    });
    return readJson(response);
  }
  async #fetchCart() {
    const cartUrl = this.dataset.cartUrl;
    if (!cartUrl) throw new VfCartChallengeError('Cart URL is missing.');
    const response = await fetch(jsonUrl(cartUrl), { headers: JSON_HEADERS, credentials: 'same-origin' });
    return readJson(response);
  }

  /** @param {Cart} cart */
  render(cart) {
    this.#syncFailed = false;
    this.#cart = cart;
    this.#renderCart(cart);
  }

  #renderCart(cart) {
    if (!this.content) return;
    this.content.replaceChildren(cart.item_count > 0 ? this.#renderFilled(cart) : this.#renderEmpty());
    const count = this.querySelector('[data-vf-cart-heading-count]');
    if (count) count.textContent = `/ ${cart.item_count}`;
    this.#clearStatus();
  }

  /** @param {Cart} cart */
  #renderFilled(cart) {
    const fragment = document.createDocumentFragment();
    const items = element('div', 'vf-cart__items');
    for (const item of cart.items) items.append(this.#renderItem(item));
    fragment.append(items, this.#renderFooter(cart));
    return fragment;
  }

  /** @param {CartItem} item */
  #renderItem(item) {
    const article = element('article', 'vf-cart__item');
    const imageLink = /** @type {HTMLAnchorElement} */ (element('a', 'vf-cart__image'));
    imageLink.href = item.url || '#';
    imageLink.tabIndex = -1;

    const imageUrl = item.featured_image?.url || item.image;
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = this.#imageUrl(imageUrl);
      image.alt = item.featured_image?.alt || item.product_title || '';
      image.loading = 'eager';
      imageLink.append(image);
    }

    const copy = element('div', 'vf-cart__item-copy');
    const title = /** @type {HTMLAnchorElement} */ (
      element('a', 'vf-cart__item-title', item.product_title || item.title)
    );
    title.href = item.url || '#';
    copy.append(title);

    if (item.variant_title && item.variant_title !== 'Default Title')
      copy.append(element('p', 'vf-cart__variant', item.variant_title));
    const price = element('p', 'vf-cart__price');
    price.append(this.#moneyElement(item.final_line_price));
    copy.append(price);

    const actions = element('div', 'vf-cart__item-actions');
    const stepper = element('div', 'vf-cart__stepper');
    stepper.setAttribute('aria-label', 'Quantity');
    const input = /** @type {HTMLInputElement} */ (element('input', 'vf-cart__quantity-input'));
    input.type = 'number';
    input.inputMode = 'numeric';
    input.min = '1';
    input.step = '1';
    input.value = String(item.quantity);
    input.dataset.vfCartQuantityInput = '';
    input.dataset.key = item.key;
    input.dataset.quantity = String(item.quantity);
    input.setAttribute('aria-label', 'Quantity');
    stepper.append(
      this.#quantityButton('−', item.key, item.quantity - 1, this.labels.decrease),
      input,
      this.#quantityButton('+', item.key, item.quantity + 1, this.labels.increase)
    );
    const remove = this.#quantityButton(this.labels.remove, item.key, 0, this.labels.remove);
    remove.className = 'vf-cart__remove';
    actions.append(stepper, remove);
    copy.append(actions);
    article.append(imageLink, copy);
    return article;
  }

  /** @param {string} text @param {string} key @param {number} quantity @param {string} label */
  #quantityButton(text, key, quantity, label) {
    const button = /** @type {HTMLButtonElement} */ (element('button', '', text));
    button.type = 'button';
    button.dataset.vfCartQuantity = '';
    button.dataset.key = key;
    button.dataset.quantity = String(Math.max(0, quantity));
    button.setAttribute('aria-label', label);
    return button;
  }

  /** @param {Cart} cart */
  #renderFooter(cart) {
    const footer = element('footer', 'vf-cart__footer');
    const subtotal = element('div', 'vf-cart__subtotal');
    const total = element('strong', '');
    total.append(this.#moneyElement(cart.total_price));
    subtotal.append(element('span', '', this.labels.subtotal), total);
    footer.append(subtotal);
    const note = element('p', '', this.labels.shipping);

    const form = /** @type {HTMLFormElement} */ (element('form', ''));
    form.action = this.dataset.cartUrl || '/cart';
    form.method = 'post';
    const checkout = /** @type {HTMLButtonElement} */ (element('button', 'vf-cart__checkout', this.labels.checkout));
    checkout.type = 'submit';
    checkout.name = 'checkout';
    form.append(checkout);

    const viewCart = /** @type {HTMLAnchorElement} */ (element('a', 'vf-cart__view-cart', this.labels.viewCart));
    viewCart.href = this.dataset.cartUrl || '/cart';
    footer.append(note, form, viewCart);
    return footer;
  }

  #renderEmpty() {
    const empty = element('div', 'vf-cart__empty');
    const mark = element('span', 'vf-cart__empty-mark');
    mark.setAttribute('aria-hidden', 'true');
    const markUrl = this.dataset.emptyMarkUrl;
    if (markUrl) {
      const image = document.createElement('img');
      image.src = markUrl;
      image.alt = '';
      image.width = 58;
      image.height = 58;
      mark.append(image);
    } else {
      mark.textContent = '0';
    }
    const title = element('h3', '', this.labels.emptyTitle);
    const text = element('p', '', this.labels.emptyText);
    const link = /** @type {HTMLAnchorElement} */ (element('a', 'vf-cart__secondary', this.labels.continue));
    link.href = '/collections/all';
    empty.append(mark, title, text, link);
    return empty;
  }

  #imageUrl(url) {
    try {
      const image = new URL(url, window.location.origin);
      if (image.hostname.endsWith('shopify.com') || image.pathname.includes('/cdn/shop/'))
        image.searchParams.set('width', '240');
      return image.href;
    } catch {
      return url;
    }
  }
  #moneyElement(cents) {
    return element('span', cents === null ? '' : 'money', cents === null ? '…' : this.#money(cents));
  }

  /** @param {number} cents */
  #money(cents) {
    return new Intl.NumberFormat(this.dataset.currency === 'USD' ? 'en-US' : this.dataset.locale || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    }).format((Number(cents) || 0) / 100);
  }

  async #restoreCartCount() {
    try {
      const cart = await this.#fetchCart();
      this.render(cart);
      this.#announceCart(cart);
      return true;
    } catch (_) {
      // The original cart request error remains the actionable failure.
      return false;
    }
  }

  /** @param {number} itemCount */
  #announceOptimistic(itemCount) {
    this.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: null, sourceId: 'vf-cart', data: { source: 'product-form-component', itemCount } },
      })
    );
  }

  /** @param {Cart} cart */
  #announceCart(cart) {
    this.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'vf-cart',
          data: { source: 'vf-cart', itemCount: cart.item_count },
        },
      })
    );
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.toggleAttribute('aria-busy', busy);
    this.querySelectorAll('[data-vf-cart-quantity], [data-vf-cart-quantity-input], .vf-cart__checkout').forEach(
      (control) => {
      control.disabled = (busy && this.#adding) || (control.matches('.vf-cart__checkout') && (busy || this.#syncFailed));
      }
    );
  }

  /** @param {string} message */
  #showError(message) {
    if (!this.status) return;
    this.status.replaceChildren(document.createTextNode(`${message} `));
    const link = /** @type {HTMLAnchorElement} */ (element('a', '', this.labels.viewCart));
    link.href = this.dataset.cartUrl || '/cart';
    this.status.append(link);
  }

  #clearStatus() {
    this.status?.replaceChildren();
  }
}

if (!customElements.get('vf-cart-drawer')) customElements.define('vf-cart-drawer', VfCartDrawer);

/** @param {FormData} formData */
export function addToVfCart(formData, options = {}) {
  const drawer = document.querySelector('vf-cart-drawer');
  if (!(drawer instanceof VfCartDrawer)) throw new VfCartChallengeError('Cart drawer is unavailable.');
  return drawer.add(formData, options);
}
